<template>
  <div class="container-fluid">
    <div class="row">
      <div class="title-container w-100 d-flex align-items-center justify-content-between">
        <h3 class="title">
          Expenses
          <div
            class="spinner-border spinner-theme_default"
            role="status"
            v-if="loading"
          >
              <span class="sr-only">
                Loading...
              </span>
          </div>
        </h3>
        <div class="title-container__controls">
          <b-button
            :variant="filterShow ? 'secondary' : 'info'"
            @click="toggleFilter"
            class="button title-container__controls-item"
          >
              <span class="material-icons icon-image-preview">
                tune
              </span>
            Filters
          </b-button>
          <b-button
            :variant="isShowBulkCreation ? 'secondary' : 'info'"
            @click="isShowBulkCreation = !isShowBulkCreation"
            class="button title-container__controls-item"
          >
                <span class="material-icons icon-image-preview">
                  tune
                </span>
            Bulk Creation
          </b-button>
          <b-button
            :disabled="loading"
            @click.prevent="editEntityModalOpen"
            class="button"
            type="button"
            v-if="$can('create-finances_expenses')"
            variant="success"
          >
            Create
          </b-button>
        </div>
      </div>

      <template v-if="$can('read_all-finances_expenses')">
        <div class="w-100">
          <Filters
            :data="filters"
            :isLoading="loading"
            @submit="getEntitiesByFilter"
            textButtonSubmit="Filter"
            v-show="filterShow"
          />
        </div>
      </template>

      <ExpensesBulkCreation
        @reloadExpenses="getEntities"
        v-show="isShowBulkCreation"
      />

      <FinancesExpensesForm
        :bankCards="bankCards"
        :currencies="currencies"
        :dataProps="lastExpensePreFillData"
        :edit="false"
        :expensesCategories="expensesCategories"
        :expensesStatuses="expensesStatuses"
        :isLoading="loading"
        :modal="true"
        :ownBankDetails="ownBankDetails"
        :projects="getProjects"
        :show-preloader="true"
        :users="getAllUsers"
        @submit="create"
        v-if="showForm"
        v-model="showForm"
      />

      <div class="col-12">
        <div class="table-wrapper">
          <TableForPage
            ref="table"
            v-if="table.data.length"
            :isLoading="loading"
            :tableData="table.data"
            :fields="table.fields"
            :slots="table.slots"
            :isEdit="$can('update_own-finances_expenses')"
            :isRemove="$can('delete_own-finances_expenses')"
          >
            <template v-slot:specialist="data">
              <div class="specialist-user-name">
                {{ data.item.user.name }}
              </div>
              <div class="specialist-user-position">
                {{ data.item.user.position }}
              </div>
            </template>
            <template v-slot:expenses_category.display_name="data">
              <div>
                <span v-if="data.item.related_project"
                     v-b-tooltip.html :title="'Related to project ' + data.item.related_project.name">
                  <font-awesome-icon icon="link" />
                </span>
                {{ data.item.expenses_category.display_name }}
              </div>
            </template>
            <template v-slot:bank_card="data">
              <LabelBankCard :card_details="getCardDetail(data.item)" />
            </template>
            <template v-slot:amount="data">
              <span>
                {{ data.item.amount ? `${data.item.amount} ${data.item.currency.code}` : '' }}
              </span>
            </template>
            <template v-slot:due_date="data">
              <span>
                {{ data.item.due_date }} / <template v-if="data.item.paid_date">{{data.item.paid_date}}</template><template v-else>&ndash;</template>
              </span>
            </template>
            <template v-slot:expenses_status="data">
              <LabelTableStatus :status="data.item.expenses_status" />
            </template>
            <template v-slot:links="data">
              <ExpensesLinks
                :item="data.item"
                @reloadExpenses="getEntities"
              />
            </template>
          </TableForPage>

          <div class="entities-table-wrapper" v-if="table.data.length > 0">
            <div>
              <div class="mt-3">
                <b-card-group deck class="mb-3">
                  <b-card border-variant="dark" class="text-center">
                    <b-card-text>
                      <h3>Original totals</h3>
                      <span v-for="(amount, currencyCode) in table.totalsData.byAllExpenses.original" :key="currencyCode">
                          {{ amount }}
                          <b-badge variant="light"> {{currencyCode}} </b-badge>
                      </span>
                    </b-card-text>
                  </b-card>

                  <b-card border-variant="dark" align="center">
                    <b-card-text>
                      <h3>Equivalent totals</h3>
                      <span v-for="(amount, currencyCode) in table.totalsData.byAllExpenses.equivalent" :key="currencyCode">
                          {{ amount }}
                          <b-badge variant="light"> {{currencyCode}} </b-badge>
                      </span>
                    </b-card-text>
                  </b-card>

                  <b-card border-variant="dark" class="details-card">
                    <b-card-text>
                      <h3 class="text-center">Details</h3>
                      <json-viewer :value="table.totalsData"></json-viewer>
                    </b-card-text>
                  </b-card>

                </b-card-group>
              </div>
            </div>

          </div>
          <div v-else>Use filters to get expenses list</div>

          <error-modal ref="errorModal" />
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import Moment from 'moment'
  import {getDateFrom} from '../functions/format_date'
  import JsonViewer from 'vue-json-viewer'
  import ExpensesLinks from '../components/ExpensesLinks'
  import FinancesExpensesForm from '../components/forms/FinancesExpensesForm'
  import FinancesMixin from '../mixins/finances'
  import ExpenseForm from '../mixins/ExpenseForm'
  import ErrorModal from '../components/modals/ErrorModal.vue'
  import Filters from '@components/Filter/Filter'
  import TableForPage from '@tables/TableForPage'
  import LabelTableStatus from '@components/tables/elements/LabelTableStatus'
  import LabelBankCard from '@components/tables/elements/LabelBankCard'
  import ExpensesBulkCreation from '../components/ExpensesBulkCreation'

  export default {
  name: 'FinancesExpenses',
  components: {
    ExpensesLinks,
    ErrorModal,
    FinancesExpensesForm,
    JsonViewer,
    Filters,
    TableForPage,
    LabelTableStatus,
    LabelBankCard,
    ExpensesBulkCreation
  },
  mixins: [FinancesMixin, ExpenseForm],
  data () {
    return {
      filterShow: true,
      loading: true,
      firstLoad: false,
      showForm: false,
      defaultFormData: {},
      editedItem: {},
      selectedFilters: {},
      lastExpensePreFillData: {},
      isShowBulkCreation: false,
      filters: {
        'date_from': {
          type: 'datepicker',
          name: 'date_from',
          label: 'Date from',
          format: 'dd/MM/yyyy',
          value: Moment().startOf('month').format('MM/DD/YYYY'),
          class: 'col-md-2'
        },
        'date_to': {
          type: 'datepicker',
          name: 'date_to',
          label: 'Date to',
          format: 'dd/MM/yyyy',
          value: Moment().endOf('month').format('MM/DD/YYYY'),
          class: 'col-md-2'
        },
        'users': {
          type: 'multiselect',
          name: 'user_id',
          label: 'Select users',
          valueField: 'id',
          selectLabel: 'label',
          options: [],
          class: 'col-md-2'
        },
        'category': {
          type: 'multiselect',
          name: 'expenses_category_id',
          label: 'Select category',
          valueField: 'id',
          selectLabel: 'display_name',
          options: [],
          class: 'col-md-2'
        },
        'status': {
          type: 'multiselect',
          name: 'expenses_status_id',
          label: 'Select status',
          valueField: 'id',
          selectLabel: 'display_name',
          options: [],
          class: 'col-md-2'
        },
        'project_related': {
          type: 'multiselect',
          name: 'project_related',
          label: 'Select type',
          valueField: 'type',
          selectLabel: 'label',
          options: [{ type: 'regular', label: 'Regular' }, { type: 'project_related', label: 'Project related' }],
          value: [],
          class: 'col-md-2'
        }
      },
      table: {
        slots: {
          'specialist': {
            name: 'specialist'
          },
          'expenses_category.display_name': {
            name: 'expenses_category.display_name'
          },
          bank_card: {
            name: 'bank_card'
          },
          amount: {
            name: 'amount'
          },
          due_date: {
            name: 'due_date'
          },
          expenses_status: {
            name: 'expenses_status'
          },
          links: {
            name: 'links'
          }
        },
        fields: [
          {
            key: 'specialist',
            label: 'specialist',
            sortable: true
          },
          {
            key: 'expenses_category.display_name',
            label: 'Category',
            sortable: true
          },
          {
            key: 'amount',
            label: 'Amount',
            sortable: true,
            class: 'column-bd-name'
          },
          {
            key: 'expenses_status',
            label: 'Status',
            sortable: true,
            class: 'column-icon text-center'
          },
          {
            key: 'bank_card',
            label: 'Bank card (Account)',
            sortable: false,
            class: 'column-bank-card text-center'
          },
          {
            key: 'comment',
            label: 'Comment'
          },
          {
            key: 'due_date',
            label: 'Due at / Paid at',
            sortable: true
          },
          {
            key: 'links',
            label: 'Actions',
            class: 'text-center'
          }
        ],
        data: [],
        totalsData: []
      }
    }
  },
  created () {
    (async () => {
      this.fetchAllData()
      await this.getEntitiesByFilter(this.filters)
      this.loading = true
      if (this.$can('read_own-finances_expenses') && !this.$can('read_all-finances_expenses')) {
        this.filter.users.push(this._UserInfo)
      }
      this.fetchFormLists().then(() => {
        // this.getLastExpensePreFillData() // alternative for loading pre fill data on each modal form open
      }).finally(() => {
        this.loading = false
      })
    })()
  },
  watch: {
    'filter.date_from': function (val) {
      if (Moment(val, 'YYYY-MM-DD').isAfter(this.filter.date_to, 'YYYY-MM-DD')) {
        this.filter.date_to = this.filter.date_from
      }
    }
  },
  methods: {
    getCardDetail (item) {
      let result = ''

      if (item.bank_card) {
        result = item.bank_card.name
      }

      if (item.bank_detail) {
        result += `\n ${item.bank_detail.summary}`
      }

      return result
    },
    async fetchAllUsers () {
      return this.$api.users.getAllUsers()
    },
    async fetchExpensesStatuses () {
      return this.$api.expenses.getExpensesStatuses()
    },
    async fetchExpensesCategories () {
      return this.$api.expenses.getExpensesCategories()
    },
    setAllOptionsFilter () {
      this.filters.status.options = this.expensesStatuses
      this.filters.category.options = this.expensesCategories
      this.filters.users.options = this.allUsers
    },
    fetchAllData () {
      Promise.all([
        this.fetchAllUsers(),
        this.fetchExpensesStatuses(),
        this.fetchExpensesCategories()
      ])
        .then(() => {
          this.setAllOptionsFilter()
        })
    },
    toggleFilter () {
      this.filterShow = !this.filterShow
    },
    resetFilter (callback) {
      this.filter.users = []
      this.filter.expensesCategories = []
      this.filter.date_from = Moment().startOf('month').format('MM/DD/YYYY')
      this.filter.date_to = Moment().endOf('month').format('MM/DD/YYYY')

      if (callback) {
        callback()
      }
    },
    getDate (date, target) {
      let result = getDateFrom(date, target)
      if (target === 'from') {
        this.filter.date_from = result
      } else {
        this.filter.date_to = result
      }
    },
    prepareDataForGetReports (fields) {
      let data = {}

      for (let key in fields) {
        const name = fields[key]['name']
        let value = fields[key]['value']

        if (Object.prototype.toString.call(value) === '[object Date]') {
          value = this._toLocalTime(value)
        }

        if (Array.isArray(value) && value.length && fields[key]['valueField']) {
          value = value.map(item => item[fields[key]['valueField']])
        }

        data[name] = value
      }

      return data
    },
    resetPagination () {
      this.$refs['table'] && this.$refs['table'].resetPagination()
    },
    async getEntitiesByFilter (fields) {
      this.selectedFilters = fields
      await this.getEntities()
      this.resetPagination()
    },
    getEntities () {
      const params = this.prepareDataForGetReports(this.selectedFilters)
      this.loading = true
      return this.$api.expenses.getExpenses(params)
        .then(response => {
          this.table.data = response.data.collection
          this.table.totalsData = response.data.total
          this.parentCategories = this.table.data.filter(cat => cat.parent_id == null)
          this.parentCategories.unshift({ id: '', 'name': 'No parent category' })
        })
        .catch(error => {
          const errors = Object.values(error.response.data.errors)
          for (var i = 0; i < errors.length; i++) {
            this.$snotify.error(
              errors[i][0],
              'Error',
              this.$store.state.snotify
            )
          }
        })
        .finally(() => {
          this.loading = false
          this.firstLoad = true
        })
    },
    async getLastExpensePreFillData () {
      return this.$api.expenses.getLastExpensePreFillData()
        .then(response => {
          const expensesCategory = this.expensesCategories.find(
            (item) => item.id === response.data.expenses_category_id
          )
          const bankCard = this.bankCards.find(
            (item) => item.id === response.data.bank_card_id
          )
          const bankDetail = this.ownBankDetails.find(
            (item) => item.id === response.data.bank_detail_id
          )
          this.lastExpensePreFillData = {
            expenses_category: expensesCategory,
            expenses_category_id: response.data.expenses_category_id,
            due_date: response.data.due_date,
            paid_date: response.data.paid_date,
            bank_card: bankCard,
            bank_card_id: response.data.bank_card_id,
            bank_detail: bankDetail,
            bank_detail_id: response.data.bank_detail_id,
            currency_id: response.data.currency_id
          }
        })
        .catch(error => {
          const errors = Object.values(error.response.data.errors)
          for (var i = 0; i < errors.length; i++) {
            this.$snotify.error(
              errors[i][0],
              'Error',
              this.$store.state.snotify
            )
          }
        })
    },
    create (data, files) {
      this.loading = true
      const params = this.prepareDataForEditAndCreateRequest(data)
      this.$api.expenses.createExpenses(params)
        .then(({ data }) => {
          this.$snotify.success(
            'Category successfully created',
            'Success',
            this.$store.state.snotify
          )

          this.showForm = false
          this.editedItem = {}
        })
        .catch(error => {
          const errors = Object.values(error.response.data.errors)
          for (var i = 0; i < errors.length; i++) {
            this.$snotify.error(
              errors[i][0],
              'Error',
              this.$store.state.snotify
            )
          }
        })
        .finally(() => {
          this.loading = false
          this.getEntities()
        })
    },
    editEntityModalOpen (data = {}) {
      this.editedItem = Object.assign({}, data)
      this.loading = true
      this.getLastExpensePreFillData()
        .finally(() => {
          this.loading = false
          this.showForm = true
        })
    }
  }
}
</script>

<style scoped>
  .table-wrapper {
    padding-top: 20px;
  }
  .table-wrapper h3 {
    margin-bottom: 30px;
  }
  .entities-table-wrapper {
    max-height: 600px;
    overflow-y: auto;
  }
  .spinner-border {
    border-width: 0.25rem !important;
  }
</style>
<style>
  .details-card.card .card-text .jv-code {
    padding: 0px;
  }
</style>
